import { ReactNode } from "react";
import { useSelector } from "react-redux";

import {
  selectProgram,
  useGetAllProgramsQuery,
  useGetEligibleEvacuationCoordinatesQuery,
  useGetEvacuationLocationsByProgramEventIdQuery,
  useGetProgramEventListQuery,
  useGetRefDataListQuery,
  useGetSessionQuery,
  useGetProgramQuery,
} from "../../store/slices/api";

interface SessionAndDataContextProps {
  children: ReactNode;
}

export const SessionAndDataContext: React.FC<SessionAndDataContextProps> = ({
  children,
}) => {
  // Redux
  const program = useSelector(selectProgram);
  const { isLoading: isSessionLoading, error: sessionError } =
    useGetSessionQuery();
  const { isLoading: isRefDataListLoading, error: refDataError } =
    useGetRefDataListQuery();
  const {
    isLoading: isEligibleEvacuationCoordinatesLoading,
    error: eligibleEvacuationCoordinatesError,
  } = useGetEligibleEvacuationCoordinatesQuery();
  const { isLoading: isProgramListLoading, error: programListError } =
    useGetAllProgramsQuery();
  const {
    data: programData,
    isLoading: isProgramLoading,
    error: programError,
  } = useGetProgramQuery();
  const {
    data: eventList,
    isLoading: isEventListLoading,
    error: eventListError,
  } = useGetProgramEventListQuery(programData?.id ?? "", {
    skip: !programData?.id,
  });

  const firstEvent =
    Array.isArray(eventList) && eventList.length > 0 ? eventList[0] : undefined;

  const {
    isLoading: isEvacuationLocationDataLoading,
    error: evacuationLocationError,
  } = useGetEvacuationLocationsByProgramEventIdQuery(firstEvent?.id ?? "", {
    skip: !firstEvent,
  }); // Use first event of first program here

  // Errors
  const isFatalEvacuationError =
    evacuationLocationError &&
    typeof evacuationLocationError === "object" &&
    "status" in evacuationLocationError &&
    evacuationLocationError.status !== 404;

  // Loading message
  if (
    isSessionLoading ||
    isRefDataListLoading ||
    isEventListLoading ||
    isEvacuationLocationDataLoading ||
    isProgramListLoading ||
    isEligibleEvacuationCoordinatesLoading ||
    isProgramLoading ||
    !program
  ) {
    return <div>Waiting for session and program data...</div>;
  }

  // Error message
  if (
    sessionError ||
    refDataError ||
    eventListError ||
    isFatalEvacuationError ||
    programListError ||
    programError ||
    eligibleEvacuationCoordinatesError
  ) {
    console.log("sessionError: ", sessionError);
    console.log("refDataError: ", refDataError);
    console.log("eventListError: ", eventListError);
    console.log("evacuationLocationError: ", evacuationLocationError);
    console.log("programListError: ", programListError);
    console.log("programError: ", programError);
    console.log(
      "eligibleEvacuationCoordinatesError: ",
      eligibleEvacuationCoordinatesError
    );

    return <div>There was an error loading data.</div>;
  }

  return <>{children}</>;
};
